import { WoDocument } from "../models/workorders/Documents";

export interface WoDocumentGroup {
  key: string;
  label: string;
  documents: WoDocument[];
}

// Documents that come back without a DocumentGroup are collected under this
// key so they can be rendered last, without a heading.
const UNGROUPED_KEY = "";

/**
 * Name of the group a document belongs to.
 *
 * GetLinkedDocInfoList already translates TranslatedDocGroup server side and
 * leaves it empty when the group has no translation, so the untranslated
 * DocumentGroup ("Work Order", "Equipment", ...) is the fallback -- the same
 * order the web group header falls back in.
 */
export const getDocumentGroupName = (document: WoDocument): string =>
  document.TranslatedDocGroup || document.DocumentGroup || "";

/**
 * Split linked documents into their document groups, keeping the order the API
 * returned them in: the record's own documents first, then the ones it inherits
 * (for a work order the equipment, serial, equipment class and system codes; for
 * equipment the serial, class and system codes; for an item its class).
 */
export const groupDocuments = (
  documents: WoDocument[],
  translations: any
): WoDocumentGroup[] => {
  const attachedToThis =
    translations?.["lbl_attached_to_this"] || "Attached to this";
  const groupsByKey = new Map<string, WoDocumentGroup>();
  const groups: WoDocumentGroup[] = [];

  (documents ?? []).forEach((document) => {
    const key = document.DocumentGroup || UNGROUPED_KEY;
    let group = groupsByKey.get(key);
    if (!group) {
      const groupName = getDocumentGroupName(document);
      group = {
        key,
        label: groupName ? `${attachedToThis} ${groupName}` : "",
        documents: [],
      };
      groupsByKey.set(key, group);
      groups.push(group);
    }
    group.documents.push(document);
  });

  // Ungrouped documents have no heading, so they read better after the ones
  // that do rather than wherever the API happened to return them.
  return [
    ...groups.filter((group) => group.key !== UNGROUPED_KEY),
    ...groups.filter((group) => group.key === UNGROUPED_KEY),
  ];
};
